import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["moduleId", "as", "verticalPositioning", "popoverPlacement", "children", "collaborationSidebarState", "onUpdateCollaborationSidebarState", "showIndicator", "showIndicatorsOnHover", "iconPosition", "iconProps", "renderCommentIndicator", "metaDescription", "userInfo", "embeddedPopoverAnchorSelector"];
import { c as _c } from "react-compiler-runtime";
import { useState, useEffect, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useEmbeddedComments from 'collaboration-sidebar/features/deepCommenting/hooks/useEmbeddedComments';
import useButtonA11yProps from 'collaboration-sidebar/hooks/useButtonA11yProps';
import useCommentableAreaStyles from 'collaboration-sidebar/features/deepCommenting/hooks/useCommentableAreaStyles';
import useUpdateMetaDescriptions from 'collaboration-sidebar/features/deepCommenting/hooks/useUpdateMetaDescriptions';
import { COMMENTABLE_AREA_IDENTIFIER } from 'collaboration-sidebar/constants/identifiers';
import EmbeddedComment from './EmbeddedComment';
import { useToggleOpenPopover } from '../hooks/useToggleOpenPopover';
import { useEmbeddedCommentHandlers } from '../hooks/useEmbeddedCommentHandlers';
import useElementResizeObserver from '../../../hooks/useElementResizeObserver';
import useWindowScrollListener from '../../../hooks/useWindowScrollListener';
import { useEmbeddedCommentChangeHandlers } from '../hooks/useEmbeddedCommentChangeHandlers';
import { useEmbeddedCommentClickListener } from '../hooks/useEmbeddedCommentClickListener';
import usePrevious from '../../../hooks/usePrevious';
import useEmbeddedCommentsNavigation from '../hooks/useEmbeddedCommentsNavigation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TD_ELEMENT_TYPE = 'TD';
const StyledHoverableCommentableArea = styled.div.withConfig({
  displayName: "EmbeddedCommentableArea__StyledHoverableCommentableArea"
})(["outline-color:rgba(255,122,89,0) !important;position:relative !important;", " ", " ", " ", " ", " ", " ", ""], ({
  outlineRadius
}) => outlineRadius && `border-radius: ${outlineRadius}px;`, ({
  showComments,
  isCommentModeActive
}) => (showComments || isCommentModeActive) && `
    transition: outline-color 0.15s ease-in, box-shadow 0.15s ease-in;
    outline: 2px solid rgba(255, 122, 89, 0) !important;
    pointer-events: auto !important;

    > td {
      pointer-events: auto !important;
    }
  `, ({
  isHighlightedBySidebar,
  outlineRadius
}) => isHighlightedBySidebar && (outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'), ({
  isTableChild,
  isDefaultElement
}) => isTableChild && isDefaultElement && `
    outline-offset: -2px;
    display: table;
    width: 100%;
  `, ({
  isTr
}) => isTr && `
    outline-offset: -2px;
  `, ({
  isCommentModeActive,
  outlineRadius
}) => isCommentModeActive && `
    &:hover, &:focus {
      cursor: crosshair;

      ${outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'}

      *:not(#comment-indicator) {
        pointer-events: none;
      }
    }
  `, ({
  isCommentModeActive,
  isCommenting,
  outlineRadius
}) => isCommentModeActive && isCommenting && `
    ${outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'}
      &:hover {
        cursor: default;
      }
    `);
function EmbeddedCommentableArea(t0) {
  "use memo";

  const $ = _c(133);
  let children;
  let collaborationSidebarState;
  let embeddedPopoverAnchorSelector;
  let iconPosition;
  let iconProps;
  let metaDescription;
  let moduleId;
  let onUpdateCollaborationSidebarState;
  let popoverPlacement;
  let propsForWrapper;
  let renderCommentIndicator;
  let t1;
  let t2;
  let t3;
  let t4;
  let userInfo;
  if ($[0] !== t0) {
    var _t = t0;
    ({
      moduleId,
      as: t1,
      verticalPositioning: t2,
      popoverPlacement,
      children,
      collaborationSidebarState,
      onUpdateCollaborationSidebarState,
      showIndicator: t3,
      showIndicatorsOnHover: t4,
      iconPosition,
      iconProps,
      renderCommentIndicator,
      metaDescription,
      userInfo,
      embeddedPopoverAnchorSelector
    } = _t);
    propsForWrapper = _objectWithoutPropertiesLoose(_t, _excluded);
    _t;
    $[0] = t0;
    $[1] = children;
    $[2] = collaborationSidebarState;
    $[3] = embeddedPopoverAnchorSelector;
    $[4] = iconPosition;
    $[5] = iconProps;
    $[6] = metaDescription;
    $[7] = moduleId;
    $[8] = onUpdateCollaborationSidebarState;
    $[9] = popoverPlacement;
    $[10] = propsForWrapper;
    $[11] = renderCommentIndicator;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = userInfo;
  } else {
    children = $[1];
    collaborationSidebarState = $[2];
    embeddedPopoverAnchorSelector = $[3];
    iconPosition = $[4];
    iconProps = $[5];
    metaDescription = $[6];
    moduleId = $[7];
    onUpdateCollaborationSidebarState = $[8];
    popoverPlacement = $[9];
    propsForWrapper = $[10];
    renderCommentIndicator = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    userInfo = $[16];
  }
  const as = t1 === undefined ? "div" : t1;
  const verticalPositioning = t2 === undefined ? false : t2;
  const showIndicator = t3 === undefined ? true : t3;
  const showIndicatorsOnHover = t4 === undefined ? false : t4;
  const [commentableArea, setCommentableArea] = useState(null);
  const [embeddedCommentWrapper, setEmbeddedCommentWrapper] = useState(null);
  const [autoOpenCommentPopover, setAutoOpenCommentPopover] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsHovering(false);
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const onRemoveComment = t5;
  const {
    isEmbeddedPopoverOpen,
    embeddedModuleID
  } = collaborationSidebarState;
  const isOpen = isEmbeddedPopoverOpen && embeddedModuleID === moduleId;
  const prevIsOpen = usePrevious(isOpen);
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = node => {
      setCommentableArea(node);
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const commentableAreaRef = t6;
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = node_0 => {
      setEmbeddedCommentWrapper(node_0);
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const embeddedCommentWrapperRef = t7;
  let t8;
  if ($[20] !== collaborationSidebarState || $[21] !== moduleId || $[22] !== onUpdateCollaborationSidebarState) {
    t8 = {
      moduleId,
      collaborationSidebarState,
      onUpdateCollaborationSidebarState,
      onRemoveComment
    };
    $[20] = collaborationSidebarState;
    $[21] = moduleId;
    $[22] = onUpdateCollaborationSidebarState;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const {
    isCommentModeActive,
    showComments,
    isCommenting,
    setIsCommenting,
    broadcastCommentsMessage,
    moduleParentComment
  } = useEmbeddedComments(t8);
  let t9;
  if ($[24] !== commentableArea || $[25] !== moduleId) {
    t9 = {
      commentableArea,
      moduleId
    };
    $[24] = commentableArea;
    $[25] = moduleId;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const {
    isHighlightedBySidebar
  } = useEmbeddedCommentsNavigation(t9);
  const prevIsCommentModeActive = usePrevious(isCommentModeActive);
  const {
    ctaUrl,
    objectType
  } = collaborationSidebarState;
  let t10;
  if ($[27] !== children || $[28] !== commentableArea) {
    t10 = {
      commentableArea,
      children
    };
    $[27] = children;
    $[28] = commentableArea;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const {
    isTableChild,
    isTr
  } = useCommentableAreaStyles(t10);
  let t11;
  if ($[30] !== broadcastCommentsMessage || $[31] !== ctaUrl || $[32] !== metaDescription || $[33] !== moduleParentComment || $[34] !== userInfo) {
    t11 = {
      userInfo,
      metaDescription,
      moduleParentComment,
      ctaUrl,
      broadcastCommentsMessage
    };
    $[30] = broadcastCommentsMessage;
    $[31] = ctaUrl;
    $[32] = metaDescription;
    $[33] = moduleParentComment;
    $[34] = userInfo;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  useUpdateMetaDescriptions(t11);
  let t12;
  if ($[36] !== commentableArea || $[37] !== embeddedCommentWrapper || $[38] !== onUpdateCollaborationSidebarState || $[39] !== popoverPlacement || $[40] !== verticalPositioning) {
    t12 = {
      commentableArea,
      popoverPlacement,
      verticalPositioning,
      embeddedCommentWrapper,
      onUpdateCollaborationSidebarState
    };
    $[36] = commentableArea;
    $[37] = embeddedCommentWrapper;
    $[38] = onUpdateCollaborationSidebarState;
    $[39] = popoverPlacement;
    $[40] = verticalPositioning;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const {
    updateIndicatorPlacement,
    updateEmbeddedCommentCoordinates,
    placement,
    topOffset,
    leftOffset
  } = useEmbeddedCommentHandlers(t12);
  let t13;
  if ($[42] !== embeddedCommentWrapper || $[43] !== embeddedPopoverAnchorSelector || $[44] !== metaDescription || $[45] !== moduleId || $[46] !== onUpdateCollaborationSidebarState || $[47] !== placement) {
    t13 = {
      embeddedCommentWrapper,
      onUpdateCollaborationSidebarState,
      moduleId,
      metaDescription,
      placement,
      embeddedPopoverAnchorSelector
    };
    $[42] = embeddedCommentWrapper;
    $[43] = embeddedPopoverAnchorSelector;
    $[44] = metaDescription;
    $[45] = moduleId;
    $[46] = onUpdateCollaborationSidebarState;
    $[47] = placement;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const toggleOpenPopover = useToggleOpenPopover(t13);
  let t14;
  if ($[49] !== updateEmbeddedCommentCoordinates || $[50] !== updateIndicatorPlacement) {
    t14 = () => {
      updateIndicatorPlacement();
      updateEmbeddedCommentCoordinates();
    };
    $[49] = updateEmbeddedCommentCoordinates;
    $[50] = updateIndicatorPlacement;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  const handleResize = t14;
  let t15;
  let t16;
  if ($[52] !== isCommenting || $[53] !== toggleOpenPopover) {
    t15 = () => {
      let autoOpenPopoverTimer;
      if (isCommenting) {
        toggleOpenPopover(true, true);
        autoOpenPopoverTimer = setTimeout(() => {
          setAutoOpenCommentPopover(true);
        }, 0);
      }
      return () => clearTimeout(autoOpenPopoverTimer);
    };
    t16 = [isCommenting, toggleOpenPopover];
    $[52] = isCommenting;
    $[53] = toggleOpenPopover;
    $[54] = t15;
    $[55] = t16;
  } else {
    t15 = $[54];
    t16 = $[55];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[56] !== isOpen || $[57] !== setIsCommenting) {
    t17 = () => {
      if (!isOpen) {
        setIsCommenting(false);
      }
      return _temp;
    };
    t18 = [isOpen, setIsCommenting];
    $[56] = isOpen;
    $[57] = setIsCommenting;
    $[58] = t17;
    $[59] = t18;
  } else {
    t17 = $[58];
    t18 = $[59];
  }
  useEffect(t17, t18);
  let t19;
  if ($[60] !== setIsCommenting || $[61] !== toggleOpenPopover) {
    t19 = () => {
      setIsCommenting(false);
      setAutoOpenCommentPopover(false);
      toggleOpenPopover(false);
    };
    $[60] = setIsCommenting;
    $[61] = toggleOpenPopover;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  const onClosePopover = t19;
  let t20;
  if ($[63] !== isCommentModeActive || $[64] !== isCommenting || $[65] !== setIsCommenting || $[66] !== showComments) {
    t20 = e => {
      if (showComments || isCommentModeActive) {
        const target = e.target instanceof HTMLElement ? e.target : null;
        const parentNode = (target === null || target === void 0 ? void 0 : target.parentNode) instanceof HTMLElement ? target.parentNode : null;
        const currentTarget = e.currentTarget instanceof HTMLElement ? e.currentTarget : null;
        if (target && currentTarget && parentNode) {
          const isActiveCommentableArea = currentTarget.id === target.id || target.nodeName === TD_ELEMENT_TYPE && parentNode.id === currentTarget.id;
          if (isCommentModeActive && !isCommenting && isActiveCommentableArea) {
            setIsCommenting(true);
          } else {
            if (isCommenting && isActiveCommentableArea) {
              e.stopPropagation();
            }
          }
        }
      }
    };
    $[63] = isCommentModeActive;
    $[64] = isCommenting;
    $[65] = setIsCommenting;
    $[66] = showComments;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  const onCommentableAreaClick = t20;
  let t21;
  bb0: {
    if (!objectType || !showIndicator) {
      t21 = false;
      break bb0;
    }
    const showIndicatorsFromHovering = showIndicatorsOnHover && (isHovering || isCommenting);
    t21 = showIndicatorsFromHovering || showComments;
  }
  const showEmbeddedComment = t21;
  let t22;
  if ($[68] !== isCommentModeActive || $[69] !== onCommentableAreaClick || $[70] !== propsForWrapper) {
    t22 = e_0 => {
      onCommentableAreaClick(e_0);
      if (!isCommentModeActive && propsForWrapper.onClick) {
        propsForWrapper.onClick(e_0);
      }
    };
    $[68] = isCommentModeActive;
    $[69] = onCommentableAreaClick;
    $[70] = propsForWrapper;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  const onClickHandler = t22;
  let t23;
  if ($[72] !== isCommentModeActive || $[73] !== onClickHandler) {
    t23 = {
      i18nKey: "collaborationSidebar.modals.commentMode.leaveComment",
      allowTabbing: isCommentModeActive,
      onClick: onClickHandler
    };
    $[72] = isCommentModeActive;
    $[73] = onClickHandler;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  const buttonA11yProps = useButtonA11yProps(t23);
  let t24;
  if ($[75] !== updateIndicatorPlacement) {
    t24 = () => {
      updateIndicatorPlacement();
    };
    $[75] = updateIndicatorPlacement;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== commentableArea || $[78] !== moduleId || $[79] !== updateIndicatorPlacement) {
    t25 = [moduleId, commentableArea, updateIndicatorPlacement];
    $[77] = commentableArea;
    $[78] = moduleId;
    $[79] = updateIndicatorPlacement;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  useEffect(t24, t25);
  useElementResizeObserver(commentableArea, handleResize);
  let t26;
  if ($[81] !== isOpen || $[82] !== updateEmbeddedCommentCoordinates) {
    t26 = {
      listenToScroll: isOpen,
      onScroll: updateEmbeddedCommentCoordinates,
      useCapture: true
    };
    $[81] = isOpen;
    $[82] = updateEmbeddedCommentCoordinates;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  useWindowScrollListener(t26);
  let t27;
  if ($[84] !== isCommentModeActive || $[85] !== prevIsCommentModeActive || $[86] !== toggleOpenPopover) {
    t27 = {
      isCommentModeActive,
      prevIsCommentModeActive,
      toggleOpenPopover
    };
    $[84] = isCommentModeActive;
    $[85] = prevIsCommentModeActive;
    $[86] = toggleOpenPopover;
    $[87] = t27;
  } else {
    t27 = $[87];
  }
  useEmbeddedCommentChangeHandlers(t27);
  let t28;
  if ($[88] !== isOpen || $[89] !== onUpdateCollaborationSidebarState || $[90] !== prevIsOpen) {
    t28 = {
      onUpdateCollaborationSidebarState,
      isOpen,
      prevIsOpen
    };
    $[88] = isOpen;
    $[89] = onUpdateCollaborationSidebarState;
    $[90] = prevIsOpen;
    $[91] = t28;
  } else {
    t28 = $[91];
  }
  useEmbeddedCommentClickListener(t28);
  const t29 = `commentable-area-${moduleId}`;
  let t30;
  if ($[92] !== isCommenting) {
    t30 = classnames(COMMENTABLE_AREA_IDENTIFIER, isCommenting && "commenting-commentable-area");
    $[92] = isCommenting;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  const t31 = as === "div";
  let t32;
  let t33;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setIsHovering(true);
    t33 = () => setIsHovering(false);
    $[94] = t32;
    $[95] = t33;
  } else {
    t32 = $[94];
    t33 = $[95];
  }
  let t34;
  if ($[96] !== autoOpenCommentPopover || $[97] !== collaborationSidebarState || $[98] !== iconPosition || $[99] !== iconProps || $[100] !== isOpen || $[101] !== isTr || $[102] !== leftOffset || $[103] !== metaDescription || $[104] !== moduleId || $[105] !== moduleParentComment || $[106] !== onClosePopover || $[107] !== onUpdateCollaborationSidebarState || $[108] !== placement || $[109] !== renderCommentIndicator || $[110] !== setIsCommenting || $[111] !== showEmbeddedComment || $[112] !== toggleOpenPopover || $[113] !== topOffset || $[114] !== userInfo || $[115] !== verticalPositioning) {
    t34 = showEmbeddedComment && /*#__PURE__*/_jsx(EmbeddedComment, {
      moduleId: moduleId,
      isOpenFromCommentMode: autoOpenCommentPopover,
      onClose: onClosePopover,
      commentableAreaRef: commentableAreaRef,
      collaborationSidebarState: collaborationSidebarState,
      verticalPositioning: verticalPositioning || isTr,
      popoverPlacement: placement,
      iconPosition: iconPosition,
      iconProps: iconProps,
      renderCommentIndicator: renderCommentIndicator,
      setIsCommenting: setIsCommenting,
      metaDescription: metaDescription,
      moduleParentComment: moduleParentComment,
      userInfo: userInfo,
      onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
      topOffset: topOffset,
      leftOffset: leftOffset,
      isOpen: isOpen,
      embeddedCommentWrapperRef: embeddedCommentWrapperRef,
      toggleOpenPopover: toggleOpenPopover
    });
    $[96] = autoOpenCommentPopover;
    $[97] = collaborationSidebarState;
    $[98] = iconPosition;
    $[99] = iconProps;
    $[100] = isOpen;
    $[101] = isTr;
    $[102] = leftOffset;
    $[103] = metaDescription;
    $[104] = moduleId;
    $[105] = moduleParentComment;
    $[106] = onClosePopover;
    $[107] = onUpdateCollaborationSidebarState;
    $[108] = placement;
    $[109] = renderCommentIndicator;
    $[110] = setIsCommenting;
    $[111] = showEmbeddedComment;
    $[112] = toggleOpenPopover;
    $[113] = topOffset;
    $[114] = userInfo;
    $[115] = verticalPositioning;
    $[116] = t34;
  } else {
    t34 = $[116];
  }
  let t35;
  if ($[117] !== as || $[118] !== buttonA11yProps || $[119] !== children || $[120] !== isCommentModeActive || $[121] !== isCommenting || $[122] !== isHighlightedBySidebar || $[123] !== isTableChild || $[124] !== isTr || $[125] !== onClickHandler || $[126] !== propsForWrapper || $[127] !== showComments || $[128] !== t29 || $[129] !== t30 || $[130] !== t31 || $[131] !== t34) {
    t35 = /*#__PURE__*/_jsxs(StyledHoverableCommentableArea, Object.assign({
      id: t29,
      className: t30,
      isCommentModeActive: isCommentModeActive,
      isCommenting: isCommenting,
      isTableChild: isTableChild,
      isTr: isTr,
      showComments: showComments,
      ref: commentableAreaRef,
      as: as,
      isDefaultElement: t31,
      isHighlightedBySidebar: isHighlightedBySidebar,
      onMouseEnter: t32,
      onMouseLeave: t33
    }, buttonA11yProps, propsForWrapper, {
      onClick: onClickHandler,
      children: [children, t34]
    }));
    $[117] = as;
    $[118] = buttonA11yProps;
    $[119] = children;
    $[120] = isCommentModeActive;
    $[121] = isCommenting;
    $[122] = isHighlightedBySidebar;
    $[123] = isTableChild;
    $[124] = isTr;
    $[125] = onClickHandler;
    $[126] = propsForWrapper;
    $[127] = showComments;
    $[128] = t29;
    $[129] = t30;
    $[130] = t31;
    $[131] = t34;
    $[132] = t35;
  } else {
    t35 = $[132];
  }
  return t35;
}
function _temp() {}
EmbeddedCommentableArea.propTypes = {
  moduleId: PropTypes.string.isRequired,
  className: PropTypes.string,
  as: PropTypes.string,
  children: PropTypes.node,
  verticalPositioning: PropTypes.bool,
  collaborationSidebarState: PropTypes.object.isRequired,
  onUpdateCollaborationSidebarState: PropTypes.func.isRequired,
  popoverPlacement: PropTypes.string,
  showIndicatorsOnHover: PropTypes.bool,
  iconPosition: PropTypes.object,
  iconProps: PropTypes.object,
  outlineRadius: PropTypes.number,
  userInfo: PropTypes.shape({
    user: PropTypes.object,
    portal: PropTypes.object
  }),
  metaDescription: PropTypes.shape({
    translationKey: PropTypes.string.isRequired,
    options: PropTypes.object
  })
};
export default EmbeddedCommentableArea;